import React, { useContext, useEffect, useState } from "react";

import dayjs from "dayjs";
import { Tabs, Spin, Empty } from "antd";
import { 
    ResponsiveContainer, 
    LineChart, 
    Line, 
    CartesianGrid, 
    XAxis, 
    YAxis, 
    Tooltip, 
    Legend 
} from "recharts";

import { Context } from "../../../context";
import "./index.css";

const LineGraph = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeMeasurement, setActiveMeasurement] = useState("count");
    const [activeTab, setActiveTab] = useState("views");
    const { token, apiUrl } = useContext(Context);
    const [granularity, setGranularity] = useState();
    const [title, setTitle] = useState();
    const [activeKey, setActiveKey] = useState('1')
    const [refetchData, setRefetchData] = useState(false);

    const [tabData, setTabData] = useState([
        { label: "Views", value: 0 },
        { label: "Unique Viewers", value: 0 },
        { label: "Playing Time", value: 0 },
        { label: "Average Playing Time", value: 0 },
        { label: "Average Completion", value: 0 },
    ]);

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.round(seconds % 60);

        let formattedTime = '';
        if (hours > 0) {
            formattedTime += `${hours}h `;
        }
        if (minutes > 0) {
            formattedTime += `${minutes}m `;
        }
        if (remainingSeconds > 0) {
            formattedTime += `${remainingSeconds}s`;
        }

        return formattedTime.trim();
    }

    const calculatePlayingTime = (totalPlayingTime) => {
        const totalPlayingTimeInHours = totalPlayingTime / 3600000;
        
        if (totalPlayingTimeInHours >= 0) {
            return totalPlayingTimeInHours.toFixed(2);
        } else {
            return "0 " 
        }
       
    }

    const calculateAveragePlayingTime = (totalPlayingTime, totalViews) => {
        const averageTimeInSeconds = totalPlayingTime / 1000 / totalViews;

        return formatTime(averageTimeInSeconds);
    }

    const calculateAverageCompletion = (value) => {
        if (value >= 0) {
            return (value * 100).toFixed(2);
        } else {
            return "0"
        }

    }

    useEffect(() => {
        setGranularity(props?.granularity);
        setTitle(props?.title);
        const fetchData = async () => {
            setLoading(true);
            try {

                // Here we are using condition url fetch based on graph type
                const urlstring = props?.graphType === "views"
                    ? `${apiUrl}/metrics/timeseries?graph_param=${activeTab}&measurement=${activeMeasurement}&timeframe=${props?.granularity}`
                    : `${apiUrl}/metrics/title/${props?.title}/timeseries?graph_param=${activeTab}&measurement=${activeMeasurement}&timeframe=${props?.granularity}`;

                const averagemetricsUrl = props?.graphType === "views"
                    ? `${apiUrl}/metrics/average-metrics?timeframe=${props?.granularity}`
                    : `${apiUrl}/metrics/title/${props?.title}/average-metrics?timeframe=${props?.granularity}`;

                const countMetricsUrl = props?.graphType === "views"
                    ? `${apiUrl}/metrics/count-metrics?timeframe=${props?.granularity}`
                    : `${apiUrl}/metrics/title/${props?.title}/count-metrics?timeframe=${props?.granularity}`;
                
                const headers = {
                    "Authorization": `Bearer ${token}`,
                    'Content-Type': 'application/json',
                };
                
                const results = await Promise.allSettled([
                    fetch(urlstring, { headers }),
                    fetch(averagemetricsUrl, { headers }),
                    fetch(countMetricsUrl, { headers }),
                ]);

                const timeseriesResult = results[0].status === "fulfilled" ? await results[0].value.json() : null;
                const averageMetricsResult = results[1].status === "fulfilled" ? await results[1].value.json() : null;
                const countMetricsResult = results[2].status === "fulfilled" ? await results[2].value.json() : null;

                if (timeseriesResult) {
                    setData(timeseriesResult?.data || []);
                }
                if (averageMetricsResult && countMetricsResult) {
                    setTabData([
                        { label: "Views", value: countMetricsResult?.data?.total_views },
                        { label: "Unique Viewers", value: countMetricsResult?.data?.value },
                        { label: "Playing Time", value: calculatePlayingTime(countMetricsResult?.data?.total_playing_time) + 'h' },
                        { label: "Average Playing Time", value: calculateAveragePlayingTime(countMetricsResult?.data?.total_playing_time, countMetricsResult?.data?.total_views) },
                        { label: "Average Completion", value: calculateAverageCompletion(averageMetricsResult?.data?.value) + '%' },
                    ]);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {

        setGranularity(props?.granularity);
        setTitle(props?.title);
 
        if (props?.granularity !== granularity || props?.title !== title || refetchData) {
            const fetchData = async () => {
                setLoading(true);
                try {

                    // Here we are using condition url fetch based on graph type
                    const urlstring = props?.graphType === "views"
                        ? `${apiUrl}/metrics/timeseries?graph_param=${activeTab}&measurement=${activeMeasurement}&timeframe=${props?.granularity}`
                        : `${apiUrl}/metrics/title/${props?.title}/timeseries?graph_param=${activeTab}&measurement=${activeMeasurement}&timeframe=${props?.granularity}`;

                    const averagemetricsUrl = props?.graphType === "views"
                        ? `${apiUrl}/metrics/average-metrics?timeframe=${props?.granularity}`
                        : `${apiUrl}/metrics/title/${props?.title}/average-metrics?timeframe=${props?.granularity}`;

                    const countMetricsUrl = props?.graphType === "views"
                        ? `${apiUrl}/metrics/count-metrics?timeframe=${props?.granularity}`
                        : `${apiUrl}/metrics/title/${props?.title}/count-metrics?timeframe=${props?.granularity}`;
                    
                    const headers = {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    };
    
                    const results = await Promise.allSettled([
                        fetch(urlstring, {headers}),
                        fetch(averagemetricsUrl, {headers}),
                        fetch(countMetricsUrl, {headers}),
                    ]);

                    const timeseriesResult = results[0].status === "fulfilled" ? await results[0].value.json() : null;
                    const averageMetricsResult = results[1].status === "fulfilled" ? await results[1].value.json() : null;
                    const countMetricsResult = results[2].status === "fulfilled" ? await results[2].value.json() : null;

                    if (timeseriesResult) {
                        setData(timeseriesResult?.data || []);
                    }
                    if (averageMetricsResult && countMetricsResult) {
                        setTabData([
                            { label: "Views", value: countMetricsResult?.data?.total_views },
                            { label: "Unique Viewers", value: countMetricsResult?.data?.value },
                            { label: "Playing Time", value: calculatePlayingTime(countMetricsResult?.data?.total_playing_time) + 'h' },
                            { label: "Average Playing Time", value: calculateAveragePlayingTime(countMetricsResult?.data?.total_playing_time, countMetricsResult?.data?.total_views) },
                            { label: "Average Completion", value: calculateAverageCompletion(averageMetricsResult?.data?.value) + '%' },
                        ]);
                    }
                } catch (err) {
                    setError(true);
                } finally {
                    setLoading(false);
                    setRefetchData(false);
                }
            };
            fetchData();
        }


    }, [activeTab, props, activeMeasurement]);

    // Transform the data to fit the Recharts format
    const transformedData = data?.length >= 1 
    ? data.map(item => ({
        time: item[0],
        value: activeKey === "5"
            ? (item[1] === null ? 0 : item[1] * 100) 
            : (item[1] === null ? 0 : item[1]),
    })) 
    : "";

    // Sampling 7 data points
    const sampleData = (data, numPoints = 7) => {
        const step = Math.floor(data.length / numPoints);
        const sampledData = [];
        for (let i = 0; i < numPoints; i++) {
            sampledData.push(data[i * step]);
        }
        return sampledData;
    };

    const formatXAxis = (value, granularity) => {
        const date = new Date(value);
        
        switch (granularity) {
            case "60:min":
                return dayjs(date).format("HH:mm");
            case "6:hours":
                return dayjs(date).format("hh:mm A");
            case "24:hours":
                return dayjs(date).format("hh:mm A");
            case "7:days":
                return dayjs(date).format("ddd, DD MMM");
            case "30:days":
                return dayjs(date).format("DD MMM");
            default:
                return dayjs(date).format("HH:mm");
        }
    };

    const xAxisData = sampleData(transformedData, 7); // Get 7 sampled data points for X-axis

    // Tooltip Formatter to show time as in X-axis
    const customTooltip = ({ payload, label }) => {
        if (payload && payload.length) {
            const formattedTime = formatXAxis(label, granularity);
            let formattedValue;

            switch (activeKey) {
                case "1":
                    formattedValue = payload[0].value;
                    return (
                        <div className="custom-tooltip">
                            <p>{`Time: ${formattedTime}`}</p>
                            <p>{`Views: ${formattedValue}`}</p>
                        </div>
                    );
                case "2":
                    formattedValue = payload[0].value;

                    return (
                        <div className="custom-tooltip">
                            <p>{`Time: ${formattedTime}`}</p>
                            <p>{`Unique Viewers: ${formattedValue}`}</p>
                        </div>
                    );
                case "3":
                    formattedValue = formatPlayingTime(payload[0].value);
                    return (
                        <div className="custom-tooltip">
                            <p>{`Time: ${formattedTime}`}</p>
                            <p>{`Playing Time: ${formattedValue}`}</p>
                        </div>
                    );
                case "4":
                    formattedValue = formatAveragePlayingTime(payload[0].value);
                    return (
                        <div className="custom-tooltip">
                            <p>{`Time: ${formattedTime}`}</p>
                            <p>{`Average Playing Time: ${formattedValue}`}</p>
                        </div>
                    );
                case "5":
                    formattedValue = `${(payload[0].value).toFixed(2)}%`;
                    return (
                        <div className="custom-tooltip">
                            <p>{`Time: ${formattedTime}`}</p>
                            <p>{`Average Completion: ${formattedValue}`}</p>
                        </div>
                    );
                default:
                    return null;
            }
        }
        return null;
    };


    const ChangeActiveTab = (tabName) => {

        if (tabName === "Views") {
            setActiveMeasurement("count");
            setActiveTab("views");
        } else if (tabName === "Unique Viewers") {
            setActiveMeasurement("count");
            setActiveTab("unique_viewers");
        } else if (tabName === "Playing Time") {
            setActiveMeasurement("sum");
            setActiveTab("playing_time");
        } else if (tabName === "Average Playing Time") {
            setActiveTab("playing_time");
            setActiveMeasurement("avg");
        } else if (tabName === "Average Completion") {
            setActiveMeasurement("avg");
            setActiveTab("video_completion_percentage");
        }
        setRefetchData(true);

    }

    const formatPlayingTime = (totalPlayingTime) => {
        const totalPlayingTimeInSeconds = totalPlayingTime / 1000; // Convert ms to seconds
        const totalPlayingTimeInMinutes = totalPlayingTimeInSeconds / 60; // Convert seconds to minutes
        const totalPlayingTimeInHours = totalPlayingTimeInMinutes / 60; // Convert minutes to hours

        return `${totalPlayingTimeInHours.toFixed(2)} h`; // 1 hour or more, show in hours
        
    };

    const formatAveragePlayingTime = (totalPlayingTime) => {
        const totalPlayingTimeInSeconds = totalPlayingTime / 1000; // Convert ms to seconds
        const totalPlayingTimeInMinutes = totalPlayingTimeInSeconds / 60; // Convert seconds to minutes
        const totalPlayingTimeInHours = totalPlayingTimeInMinutes / 60; // Convert minutes to hours
        
        return `${totalPlayingTimeInMinutes.toFixed(2)} m`;
    }

    const getYAxisTicks = () => {
        if (!transformedData.length) return [];

        switch (activeKey) {
            case "1": {
                const values = transformedData.map(item => item.value);
                const max = Math.max(...values);
                return Array.from({ length: 5 }, (_, i) => Math.round(max * i / 4));
            }
            case "2": {
                const values = transformedData.map(item => item.value);
                const max = Math.max(...values);
                const ticks = Array.from({ length: 5 }, (_, i) => Math.round(max * i / 4));
                return ticks.map(tick => tick % 2 === 0 ? tick : tick + 1);
            }
            case "3":
            case "4": {
                const values = transformedData.map(item => item.value);
                const max = Math.max(...values);
                return Array.from({ length: 5 }, (_, i) => Math.round(max * i / 4));
            }
            default:
                return [];
        }
    };

    const getTickFormatter = (value) => {
        switch (activeKey) {
            case "1":
                return value;
            case "2":
                return value;
            case "3":
                return formatPlayingTime(value)
            case "4":
                return formatAveragePlayingTime(value)
            default:
                return value+ "%";
        }
    };

    return (
        <div className="line-graph-container">
            {loading ? (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <Spin tip="Loading..." />
                </div>
            ) : (
                <>
                    <Tabs
                        type="card"
                        style={{
                            marginBottom: 32,
                            marginLeft: 20,
                        }}
                        activeKey={activeKey}
                        className="custom-tabs"
                        items={tabData?.map((tab, index) => {
                            const id = String(index + 1);
                            return {
                                label: (
                                    <div>
                                        <div>{tab.label}</div>
                                        <div className="value">{tab.value}</div>
                                    </div>
                                ),
                                key: id,
                            };
                        })}
                        onChange={(activeKey) => {
                            setActiveKey(activeKey);
                            const activeTab = tabData[parseInt(activeKey, 10) - 1];
                            ChangeActiveTab(activeTab?.label);
                            setLoading(true);
                        }}
                    />
                    <div>
                        {error || transformedData.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                                <Empty description="No Data Available" />
                            </div>
                        ) : (
                            <div style={{ maxWidth: "1000px" }}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={transformedData}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="time"
                                            tickFormatter={(value) => formatXAxis(value, granularity)}
                                            ticks={xAxisData?.map(item => item?.time)}
                                            domain={["auto", "dataMax"]}
                                            strokeDasharray="3 3"
                                            tick={{ fontSize: 12 }}
                                        />
                                           <YAxis
                                            tickFormatter={getTickFormatter}
                                            orientation="right"
                                            ticks={getYAxisTicks()}
                                            domain={["auto", "auto"]}
                                            strokeDasharray="3 3"
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip content={customTooltip} />
                                        <Legend />
                                        <Line
                                            type="linear"
                                            dataKey="value"
                                            stroke="#ff6100"
                                            strokeWidth={1.5}
                                            activeDot={{ r: 4 }}
                                            name="Views"
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default LineGraph;
